import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import VideoEpisodes from '../components/VideoEpisodes';
import VideoPlayerLayout from '../components/VideoPlayerLayout';
import VideoElement from '../components/VideoElement';
import { useVideoPlayer } from '../hooks/useVideoPlayer';
import AdBanner from '../components/AdBanner';
import useAdFree from '../hooks/useAdFree';
import { getContentById } from '../services/contentService';

interface VideoPlayerProps {
  videoId?: string;
  title?: string;
  thumbnail?: string;
  subtitles?: string[];
  creatorId?: string;
  contentId?: string;
  uploaderName?: string;
  creatorAvatar?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  videoId = '', 
  title = 'Video Player',
  thumbnail = '',
  subtitles = [],
  creatorId = '',
  contentId = '',
  uploaderName = 'Creator',
  creatorAvatar = ''
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdFree } = useAdFree();
  const [showPreRoll, setShowPreRoll] = useState<boolean>(false);
  const [showPostRoll, setShowPostRoll] = useState<boolean>(false);
  const [contentCategory, setContentCategory] = useState<string | null>(null);

  const videoPlayerState = useVideoPlayer({ 
    videoId, 
    contentId, 
    userUid: user?.uid,
    title,
    creatorId
  });

  const {
    isPlaying, currentTime, duration, isDragging, showSkipFeedback, skipAmount,
    hoverTime, showPreview, bufferedRanges, showControls, likes, dislikes,
    viewCount, subscriberCount, isLiked, isDisliked, isSubscribed, playbackSpeed, isLocked, showEpisodes,
    showSubtitles, isMuted, isFullscreen, episodes,
    isLoadingEpisodes, videoRef, formatTime, isValidVideoSource,
    getProgressPercentage, handlePlayPause, handleProgressMouseMove,
    handleProgressMouseLeave, handleProgressClick, getBufferedPercent,
    handleSkip, handleLike, handleDislike, handleSubscribe, handleShare,
    handleDonate, handleLockToggle, handleEpisodesToggle, handleSubtitlesToggle,
    handleMuteToggle, handleFullscreenToggle, handleDownload, handleRefresh,
    showControlsTemporarily, fetchCreatorStats, fetchSubscriptionStatus, getCreatorAvatar, setCurrentTime, setDuration, setBufferedRanges,
    setShowControls, setPlaybackSpeed, setShowEpisodes,
    setIsPlaying, setIsLoading
  } = videoPlayerState;

  useEffect(() => {
    // Show pre-roll ad only once on mount if not ad-free
    if (!isAdFree) {
      setShowPreRoll(true);
      setIsPlaying(false);
    }
  }, [isAdFree, setIsPlaying]);

  useEffect(() => {
    const handleMouseMove = () => showControlsTemporarily();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'L' && e.shiftKey) { e.preventDefault(); handleLockToggle(); return; }
      if (isLocked) return;
      const keyActions: Record<string, () => void> = {
        ' ': handlePlayPause, 'k': handlePlayPause, 'ArrowLeft': () => handleSkip(-10),
        'ArrowRight': () => handleSkip(10), 'ArrowUp': () => handleSkip(30),
        'ArrowDown': () => handleSkip(-30), 'j': () => handleSkip(-10),
        'l': () => handleSkip(10), 'f': handleFullscreenToggle, 'm': handleMuteToggle,
        'c': handleSubtitlesToggle, 's': handleEpisodesToggle
      };
      const action = keyActions[e.key];
      if (action) { e.preventDefault(); action(); }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showControlsTemporarily, handleLockToggle, isLocked, handlePlayPause, handleSkip, handleFullscreenToggle, handleMuteToggle, handleSubtitlesToggle, handleEpisodesToggle]);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      const ensureSubtitlesVisible = () => {
        try {
          // Prefer showing the first track by default when subtitles are provided
          if (subtitles && subtitles.length > 0 && video.textTracks && video.textTracks.length > 0) {
            for (let i = 0; i < video.textTracks.length; i++) {
              video.textTracks[i].mode = i === 0 ? 'showing' : 'disabled';
            }
          }
        } catch (_) {
          // no-op
        }
      };
      const handlers = {
        timeupdate: () => setCurrentTime(video.currentTime),
        durationchange: () => setDuration(video.duration),
        progress: () => setBufferedRanges(video.buffered),
        play: () => { setIsPlaying(true); setIsLoading(false); },
        pause: () => setIsPlaying(false),
        loadstart: () => setIsLoading(true),
        canplay: () => { setIsLoading(false); ensureSubtitlesVisible(); },
        loadedmetadata: () => ensureSubtitlesVisible(),
        loadeddata: () => ensureSubtitlesVisible(),
        ended: () => {
          if (!isAdFree) setShowPostRoll(true);
        }
      };

      Object.entries(handlers).forEach(([event, handler]) => {
        video.addEventListener(event, handler);
      });

      return () => {
        Object.entries(handlers).forEach(([event, handler]) => {
          video.removeEventListener(event, handler);
        });
      };
    }
  }, [videoRef, setCurrentTime, setDuration, setBufferedRanges, videoPlayerState, subtitles, isAdFree]);

  // Load creator stats and subscription status when component mounts
  useEffect(() => {
    fetchCreatorStats();
    fetchSubscriptionStatus();
  }, [fetchCreatorStats, fetchSubscriptionStatus]);

  // Fetch content category to determine if episodes should be shown
  useEffect(() => {
    const fetchContentCategory = async () => {
      if (contentId) {
        try {
          const content = await getContentById(contentId);
          if (content) {
            setContentCategory(content.category);
          }
        } catch (error) {
          console.error('Error fetching content category:', error);
        }
      }
    };
    fetchContentCategory();
  }, [contentId]);

  const handleSpeedChange = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const newSpeed = speeds[(speeds.indexOf(playbackSpeed) + 1) % speeds.length];
    setPlaybackSpeed(newSpeed);
    if (videoRef.current) videoRef.current.playbackRate = newSpeed;
  };

  return (
    <>
      <Box
        position="relative"
        width="100%"
        height="0"
        paddingBottom="45%"
        maxW="1600px"
        mx="auto"
      mt={{ base: 4, md: 6, lg: 8 }}
      px={{ base: 4, md: 6, lg: 8 }}
        bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
        overflow="hidden"
        onMouseMove={showControlsTemporarily}
        onMouseLeave={() => setShowControls(false)}
      >
        {showPreRoll && (
          <AdBanner
            type="pre-roll"
            autoClose
            duration={5}
            onSkip={() => {
              setShowPreRoll(false);
              setIsPlaying(true);
              if (videoRef.current) videoRef.current.play();
            }}
            onClose={() => setShowPreRoll(false)}
          />
        )}
        <VideoElement
          videoId={videoId}
          title={title}
          thumbnail={thumbnail}
          subtitles={subtitles}
          isValidVideoSource={isValidVideoSource}
          videoRef={videoRef}
        />

        <VideoPlayerLayout
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          isDragging={isDragging}
          hoverTime={hoverTime}
          showPreview={showPreview}
          bufferedRanges={bufferedRanges}
          showControls={showControls}
          isLocked={isLocked}
          showSkipFeedback={showSkipFeedback}
          skipAmount={skipAmount}
          isLiked={isLiked}
          isDisliked={isDisliked}
          isSubscribed={isSubscribed}
              likes={likes}
              dislikes={dislikes}
              viewCount={viewCount}
              creatorAvatar={getCreatorAvatar(creatorAvatar)}
          uploaderName={uploaderName}
          subscriberCount={subscriberCount}
          creatorId={creatorId}
          userUid={user?.uid}
          showEpisodes={showEpisodes && contentCategory !== 'movie'}
          showSubtitles={showSubtitles}
          playbackSpeed={playbackSpeed}
          isMuted={isMuted}
          isFullscreen={isFullscreen}
          formatTime={formatTime}
          getProgressPercentage={getProgressPercentage}
          getBufferedPercent={getBufferedPercent}
          handlePlayPause={handlePlayPause}
          handleSkip={handleSkip}
          handleProgressMouseMove={handleProgressMouseMove}
          handleProgressMouseLeave={handleProgressMouseLeave}
          handleProgressClick={handleProgressClick}
          handleLike={handleLike}
          handleDislike={handleDislike}
          handleSubscribe={handleSubscribe}
          handleShare={handleShare}
          handleDonate={handleDonate}
          handleMuteToggle={handleMuteToggle}
          handleFullscreenToggle={handleFullscreenToggle}
          handleDownload={handleDownload}
          handleRefresh={handleRefresh}
          handleEpisodesToggle={handleEpisodesToggle}
          handleSubtitlesToggle={handleSubtitlesToggle}
          handleSpeedChange={handleSpeedChange}
          handleLockToggle={handleLockToggle}
          onNavigateBack={() => navigate(-1)}
          title={title}
        />
      </Box>
      {showPostRoll && (
        <AdBanner
          type="mid-roll"
          autoClose
          duration={5}
          onSkip={() => setShowPostRoll(false)}
          onClose={() => setShowPostRoll(false)}
        />
      )}

      {contentCategory !== 'movie' && (
        <VideoEpisodes
          showEpisodes={showEpisodes}
          episodes={episodes}
          isLoadingEpisodes={isLoadingEpisodes}
          onClose={() => setShowEpisodes(false)}
          onEpisodeClick={(episode) => {
                        navigate(`/player/${episode.id}`, {
                          state: {
                            src: episode.assets?.video || '',
                            title: episode.title,
                            subtitles: episode.assets?.subtitles ? 
                              (Array.isArray(episode.assets.subtitles) ? episode.assets.subtitles : episode.assets.subtitles.split(',')) : [],
                            creatorId: episode.uploaderId,
                            contentId: episode.id,
                            uploaderName: episode.uploader
                          }
                        });
                      }}
        />
      )}

    </>
  );
};

export default VideoPlayer;
